import {
    Button,
    Dropdown,
    Input,
    Label,
    Link,
    Option,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    Toolbar,
    ToolbarButton,
    ToolbarGroup,
    useId,
} from '@fluentui/react-components';
import React, { useCallback, useMemo } from 'react';
import { useCustomerUpdatesToolbarStyles } from './CustomerUpdatesToolbar.styles';
import {
    Dismiss24Regular,
    Filter24Regular,
    Search24Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import {
    CustomerUpdatesFiltersState,
    customerUpdatesState,
    CustomerNotificationFilterStatus,
    getEmptyCustomerUpdatesFilters,
} from '../../../store/features/customerUpdates/customerUpdatesSlice';
import { useSelector } from 'react-redux';
import { useTruthyCount } from '../../../hooks/useTruthyCount';
import { customerUpdatesDataConstraints } from '../../../store/features/customerUpdates/customerUpdates.thunks';

const statusFilterValues: readonly CustomerNotificationFilterStatus[] = [
    'NotNotified',
    'Notified',
] as const;

interface CustomerUpdatesToolbarProps {
    onFilterUpdate: (filter: Partial<CustomerUpdatesFiltersState>) => void;
    onSearchQueryUpdate: (query: string) => void;
}

const CustomerUpdatesToolbar = React.memo(
    ({ onFilterUpdate, onSearchQueryUpdate }: CustomerUpdatesToolbarProps) => {
        const { t } = useTranslation();
        const styles = useCustomerUpdatesToolbarStyles();

        const {
            viewType,
            availableFilters,
            queryParams: { filters },
        } = useSelector(customerUpdatesState);

        const [searchQuery, setSearchQuery] = React.useState<string>('');

        const onClearCallback = useCallback(() => {
            onFilterUpdate(getEmptyCustomerUpdatesFilters());
        }, [onFilterUpdate]);

        const onFilterUpdateCallback = useCallback(
            (filter: Partial<CustomerUpdatesFiltersState>) => {
                onFilterUpdate(filter);
            },
            [onFilterUpdate]
        );

        const onQueryTextChange = useCallback(
            (value: string) => {
                value = value.slice(
                    0,
                    customerUpdatesDataConstraints.maxQueryTextLength
                );
                setSearchQuery(value);
                onSearchQueryUpdate(value);
            },
            [onSearchQueryUpdate]
        );

        const appliedFiltersCount = useTruthyCount([
            filters.loyalties?.length,
            filters.cabins?.length,
            filters.baggageStatus,
            filters.misconnectedCustomerStatus,
        ]);

        const buttonFilterLabel = useMemo(() => {
            let label = t('Button_Filter');
            if (appliedFiltersCount > 0) {
                label = `${label} (${appliedFiltersCount})`;
            }
            return label;
        }, [appliedFiltersCount, t]);

        const filtersToDisplay = useMemo<{
            bags: boolean;
            misconnectedCustomers: boolean;
        }>(() => {
            return {
                bags: viewType === 'All' || viewType === 'MishandledBaggage',
                misconnectedCustomers:
                    viewType === 'All' || viewType === 'MisconnectedCustomers',
            };
        }, [viewType]);

        const filterIds = {
            baggageStatus: useId(),
            misconnectedCustomers: useId(),
            messageStatus: useId(),
            cabin: useId(),
            loyalty: useId(),
        };

        return (
            <Toolbar aria-label="Flights" className={styles.toolbar}>
                <ToolbarGroup role="presentation"></ToolbarGroup>
                <ToolbarGroup role="presentation">
                    <Popover>
                        <PopoverTrigger disableButtonEnhancement>
                            <ToolbarButton
                                aria-label={t('Button_Filter')!}
                                appearance="subtle"
                                icon={<Filter24Regular />}
                            >
                                {buttonFilterLabel}
                            </ToolbarButton>
                        </PopoverTrigger>
                        <PopoverSurface>
                            <div className={styles.filtersContainer}>
                                <div className={styles.filtersHeader}>
                                    <Link
                                        appearance="subtle"
                                        disabled={appliedFiltersCount === 0}
                                        onClick={onClearCallback}
                                    >
                                        {t('Button_Filter_ClearAll')}
                                    </Link>
                                </div>

                                <div className={styles.filters}>
                                    <div className={styles.filter}>
                                        <Label htmlFor={filterIds.cabin}>
                                            Cabins
                                        </Label>
                                        <Dropdown
                                            aria-labelledby={filterIds.cabin}
                                            multiselect
                                            value={filters.cabins.join(', ')}
                                            selectedOptions={filters.cabins}
                                            onOptionSelect={(_, data) =>
                                                onFilterUpdateCallback({
                                                    cabins: [
                                                        ...data.selectedOptions,
                                                    ],
                                                })
                                            }
                                        >
                                            {availableFilters.cabins.map(
                                                (cabin) => (
                                                    <Option
                                                        key={cabin}
                                                        value={cabin}
                                                        text={cabin}
                                                    >
                                                        {cabin}
                                                    </Option>
                                                )
                                            )}
                                        </Dropdown>
                                    </div>
                                    {filtersToDisplay.bags && (
                                        <div className={styles.filter}>
                                            <Label
                                                htmlFor={filterIds.baggageStatus}
                                            >
                                                Mishandled baggage status
                                            </Label>
                                            <Dropdown
                                                aria-labelledby={
                                                    filterIds.baggageStatus
                                                }
                                                clearable
                                                value={
                                                    filters.baggageStatus
                                                        ? (t(
                                                              `CustomerItemStatus_${filters.baggageStatus}`
                                                          ) ?? '')
                                                        : ''
                                                }
                                                selectedOptions={
                                                    filters.baggageStatus
                                                        ? [
                                                              filters.baggageStatus,
                                                          ]
                                                        : []
                                                }
                                                onOptionSelect={(_, data) =>
                                                    onFilterUpdateCallback({
                                                        baggageStatus:
                                                            (data.optionValue as CustomerNotificationFilterStatus) ||
                                                            null,
                                                    })
                                                }
                                            >
                                                {statusFilterValues.map(
                                                    (status) => (
                                                        <Option
                                                            key={status}
                                                            value={status}
                                                        >
                                                            {t(
                                                                `CustomerItemStatus_${status}`
                                                            ) ?? ''}
                                                        </Option>
                                                    )
                                                )}
                                            </Dropdown>
                                        </div>
                                    )}
                                    {filtersToDisplay.misconnectedCustomers && (
                                        <div className={styles.filter}>
                                            <Label
                                                htmlFor={
                                                    filterIds.misconnectedCustomers
                                                }
                                            >
                                                Misconnected customers status
                                            </Label>
                                            <Dropdown
                                                aria-labelledby={
                                                    filterIds.misconnectedCustomers
                                                }
                                                clearable
                                                value={
                                                    filters.misconnectedCustomerStatus
                                                        ? (t(
                                                              `CustomerItemStatus_${filters.misconnectedCustomerStatus}`
                                                          ) ?? '')
                                                        : ''
                                                }
                                                selectedOptions={
                                                    filters.misconnectedCustomerStatus
                                                        ? [
                                                              filters.misconnectedCustomerStatus,
                                                          ]
                                                        : []
                                                }
                                                onOptionSelect={(_, data) =>
                                                    onFilterUpdateCallback({
                                                        misconnectedCustomerStatus:
                                                            (data.optionValue as CustomerNotificationFilterStatus) ||
                                                            null,
                                                    })
                                                }
                                            >
                                                {statusFilterValues.map(
                                                    (status) => (
                                                        <Option
                                                            key={status}
                                                            value={status}
                                                        >
                                                            {t(
                                                                `CustomerItemStatus_${status}`
                                                            ) ?? ''}
                                                        </Option>
                                                    )
                                                )}
                                            </Dropdown>
                                        </div>
                                    )}
                                    <div className={styles.filter}>
                                        <Label htmlFor={filterIds.messageStatus}>
                                            Message status
                                        </Label>
                                        <Dropdown
                                            aria-labelledby={filterIds.messageStatus}
                                            clearable
                                            value={
                                                filters.messageStatus
                                                    ? (t(
                                                          `CustomerItemStatus_${filters.messageStatus}`
                                                      ) ?? '')
                                                    : ''
                                            }
                                            selectedOptions={
                                                filters.messageStatus
                                                    ? [filters.messageStatus]
                                                    : []
                                            }
                                            onOptionSelect={(_, data) =>
                                                onFilterUpdateCallback({
                                                    messageStatus:
                                                        (data.optionValue as CustomerNotificationFilterStatus) ||
                                                        null,
                                                })
                                            }
                                        >
                                            {statusFilterValues.map(
                                                (status) => (
                                                    <Option
                                                        key={status}
                                                        value={status}
                                                    >
                                                        {t(
                                                            `CustomerItemStatus_${status}`
                                                        ) ?? ''}
                                                    </Option>
                                                )
                                            )}
                                        </Dropdown>
                                    </div>
                                    <div className={styles.filter}>
                                        <Label htmlFor={filterIds.loyalty}>
                                            {t("Passenger_LoyaltyLevel")}
                                        </Label>
                                        <Dropdown
                                            aria-labelledby={filterIds.loyalty}
                                            multiselect
                                            value={
                                                filters.loyalties.join(', ') ??
                                                ''
                                            }
                                            selectedOptions={filters.loyalties}
                                            onOptionSelect={(_, data) =>
                                                onFilterUpdateCallback({
                                                    loyalties: [
                                                        ...data.selectedOptions,
                                                    ],
                                                })
                                            }
                                        >
                                            {availableFilters.frequentFlyerTiers.map(
                                                (loyalty) => (
                                                    <Option
                                                        key={loyalty}
                                                        value={loyalty}
                                                    >
                                                        {loyalty}
                                                    </Option>
                                                )
                                            )}
                                        </Dropdown>
                                    </div>
                                </div>
                            </div>
                        </PopoverSurface>
                    </Popover>
                    <Input
                        placeholder={t('SearchFlightsInputPlaceholder')!}
                        size="small"
                        style={{ maxWidth: '200px' }}
                        contentAfter={
                            <Button
                                appearance="transparent"
                                onClick={() => onQueryTextChange('')}
                                icon={
                                    !searchQuery ? (
                                        <Search24Regular />
                                    ) : (
                                        <Dismiss24Regular />
                                    )
                                }
                            />
                        }
                        onChange={(_, data) => onQueryTextChange(data.value)}
                        value={searchQuery}
                    />
                </ToolbarGroup>
            </Toolbar>
        );
    }
);

export default CustomerUpdatesToolbar;
